import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

/**
 * A caller's own dial blocks (BACKLOG C-2; backend GLE-8254 and GLE-8273).
 * Every route is behind the backend kill switch `dial_blocks_enabled`: while
 * it is off no route is registered and each one answers 404 (error_code
 * 1035), which the rep app reads as "My Week is not switched on".
 */

export const DIAL_BLOCK_STATUSES = {
  SCHEDULED: "scheduled",
  COMPLETED: "completed",
  NO_SHOW: "no_show",
  CANCELLED: "cancelled",
} as const;

export type DialBlockStatusI =
  (typeof DIAL_BLOCK_STATUSES)[keyof typeof DIAL_BLOCK_STATUSES];

export const DIAL_BLOCK_OUTCOMES = {
  POWER: "power",
  WIN: "win",
  EFFORT: "effort",
  FUMBLE: "fumble",
} as const;

export type DialBlockOutcomeI =
  (typeof DIAL_BLOCK_OUTCOMES)[keyof typeof DIAL_BLOCK_OUTCOMES];

/**
 * What the platform counted for a block (B4): null until it is first graded,
 * about 15-25 minutes after it ends. Until `final` (24 h after the end) the
 * numbers, the status and the outcome may still move.
 */
export interface DialBlockMetricsI {
  dials: number;
  pitches: number;
  meetings: number;
  standard_dials: number;
  standard_pitches: number;
  standard_meetings: number;
  computed_at: string;
  final: boolean;
}

export interface DialBlockI {
  id: string;
  campaign_id: string;
  campaign_name?: string | null;
  /** UTC ISO strings; the end is always the start plus 3 h 30 min. */
  start_time: string;
  end_time: string;
  status: DialBlockStatusI;
  /** Set once graded: power, win, effort or fumble (null on a no-show). */
  outcome?: DialBlockOutcomeI | null;
  metrics?: DialBlockMetricsI | null;
}

/**
 * A campaign the caller can book, with what is left of it that ET week
 * (B2b, GLE-8277). `null` limits mean no limit; `left` is the lower of the
 * campaign's and the caller's own, floored at 0.
 */
export interface DialBlockCampaignI {
  campaign_id: string;
  campaign_name: string | null;
  weekly_capacity: number | null;
  booked: number;
  my_weekly_cap: number | null;
  my_booked: number;
  left: number | null;
  /** Whole ET hours: the first start and the latest end. */
  dialing_start_hour_et: number;
  dialing_end_hour_et: number;
  /** ISO weekdays the campaign calls on, Monday = 1 to Saturday = 6. */
  call_days: Array<number>;
}

export const AVAILABILITY_REASON_CATEGORIES = {
  SCHEDULED_CONFLICT: "scheduled_conflict",
  FAMILY_EMERGENCY: "family_emergency",
  ILLNESS: "illness",
  TRAVEL: "travel",
  OTHER: "other",
} as const;

export type AvailabilityReasonCategoryI =
  (typeof AVAILABILITY_REASON_CATEGORIES)[keyof typeof AVAILABILITY_REASON_CATEGORIES];

export const AVAILABILITY_REQUEST_STATUSES = {
  PENDING: "pending",
  APPROVED: "approved",
  DENIED: "denied",
} as const;

export type AvailabilityRequestStatusI =
  (typeof AVAILABILITY_REQUEST_STATUSES)[keyof typeof AVAILABILITY_REQUEST_STATUSES];

/** The caller's request to lower one week's commitment (B2c, GLE-8284). */
export interface DialBlockAvailabilityRequestI {
  id: string;
  week_start: string;
  reason_category: AvailabilityReasonCategoryI;
  reason_notes: string | null;
  /** The commitment asked for that week (absolute). */
  reduced_blocks: number;
  status: AvailabilityRequestStatusI;
  review_notes: string | null;
  reviewed_at: string | null;
  /**
   * Decisions on earlier requests about the same week, newest first (C-12
   * B). Absent until its backend is deployed, which hides "Send another
   * update".
   */
  earlier?: DialBlockAvailabilityDecisionI[];
}

/** An earlier reviewed request about a week, replaced by a later one. */
export interface DialBlockAvailabilityDecisionI {
  status: AvailabilityRequestStatusI;
  reason_category: AvailabilityReasonCategoryI;
  reason_notes: string | null;
  reduced_blocks: number;
  review_notes: string | null;
  reviewer_name: string | null;
  reviewed_at: string;
  requested_at: string;
}

/**
 * The caller's request to change their usual weekly commitment (C-12 B,
 * GLE-8399). An approved one starts on `effective_week_start`, the week
 * after the LGA approved it.
 */
export interface DialBlockUsualCommitmentRequestI {
  id: string;
  /** The usual commitment asked for, 1 to 24. */
  blocks_per_week: number;
  reason_notes: string | null;
  status: AvailabilityRequestStatusI;
  /** The ET Monday an approved request starts on; null unless approved. */
  effective_week_start: string | null;
  review_notes: string | null;
  reviewed_at: string | null;
  created_at: string;
}

/** The caller's commitment for the week (B2c, GLE-8284). */
export interface DialBlockCommitmentI {
  /**
   * The caller's usual commitment that week: 10 unless their LGA set 1 to
   * 24 or approved a usual commitment request from an earlier week.
   */
  blocks_per_week: number;
  /** The week's: an approved request's `reduced_blocks`, else the default. */
  week_commitment: number;
  is_reduced: boolean;
  /** The caller's live blocks that ET week, on every campaign. */
  booked: number;
  /** `blocks_per_week` + 3, Command Center's ceiling. */
  max_bookable: number;
  /** The caller's request about this week, if any. */
  request: DialBlockAvailabilityRequestI | null;
  /**
   * The caller's latest usual commitment request. Absent until C-12 B's
   * routes are deployed.
   */
  usual_request?: DialBlockUsualCommitmentRequestI | null;
  /**
   * The caller's usual commitment next ET calendar week, whichever week was
   * asked for (C-12 B): what a usual commitment request changes.
   */
  usual_next_week?: number;
}

export interface ListDialBlocksResponseI {
  week_start: string;
  dial_blocks: Array<DialBlockI> | null;
  /** Absent until B2b's routes are deployed. */
  campaigns?: Array<DialBlockCampaignI> | null;
  /** Absent until B2c's routes are deployed. */
  commitment?: DialBlockCommitmentI | null;
}

export interface CreateAvailabilityRequestI {
  /** The ET Monday: this week or one of the next 4. */
  week_start: string;
  reason_category: AvailabilityReasonCategoryI;
  reason_notes?: string;
  /** 0 to the week's commitment − 1. */
  reduced_blocks: number;
}

export interface AvailabilityRequestResponseI {
  request: DialBlockAvailabilityRequestI;
}

export interface CreateUsualCommitmentRequestI {
  /** 1 to 24, not the usual commitment the caller has from next week. */
  blocks_per_week: number;
  reason_notes?: string;
}

export interface UsualCommitmentRequestResponseI {
  request: DialBlockUsualCommitmentRequestI;
}

/** The caller's current score (B6, GLE-8288): their latest scored week's. */
export interface DialBlockScoreSummaryI {
  score: number;
  /** A, B, C or D. */
  letter_grade: string;
  /** up, stable or down. */
  trend: string;
  /** The ET Monday of the week the score comes from. */
  week_start: string;
  is_grace: boolean;
}

/** One scored week, with Command Center's column names (penalties positive). */
export interface DialBlockWeeklyScoreI {
  week_start: string;
  required_blocks: number;
  blocks_scheduled: number;
  blocks_completed: number;
  remaining_blocks: number;
  extra_blocks_count: number;
  trouble_blocks_count: number;
  on_time_blocks_count: number;
  streak_consecutive_blocks: number;
  base_score: number;
  total_block_base_points: number;
  total_metric_bonuses: number;
  total_timeliness_bonuses: number;
  total_streak_multiplier_effect: number;
  weekly_completion_bonus: number;
  remaining_penalty: number;
  zero_metric_penalty: number;
  net_weekly_change: number;
  adjustment_points: number;
  weekly_score: number;
  letter_grade: string;
  trend: string;
  is_grace: boolean;
  computed_at: string;
  /** The week is frozen. */
  final: boolean;
}

export interface DialBlockScoreAdjustmentI {
  id: string;
  week_start: string;
  adjustment_points: number;
  reason: string;
  adjusted_by_name: string | null;
  created_at: string;
}

/**
 * The caller's score, latest 12 weeks and latest 20 adjustments, newest
 * first. `score` is null until their first week is scored (CC's 85 / B /
 * stable).
 */
export interface DialBlockScoreResponseI {
  score: DialBlockScoreSummaryI | null;
  weeks: Array<DialBlockWeeklyScoreI> | null;
  adjustments: Array<DialBlockScoreAdjustmentI> | null;
}

export interface DialBlockResponseI {
  dial_block: DialBlockI;
}

/**
 * A 404 here means "switched off": an answer, not an error to log. A fresh
 * object per request, because the meta interceptor writes into `meta`.
 */
const switchProbeConfig = (): AxiosRequestConfig &
  AxiosInstanceInterceptorRequestExtensionI => ({
  meta: { expectedStatuses: [404] },
});

export const DialBlocksAPIEndpoints = (api: AxiosInstance) => ({
  /** The caller's live blocks in one ET week; the current week when omitted. */
  listWeek: (weekStart?: string) =>
    api.get<ListDialBlocksResponseI>("/v1/dial_blocks", {
      ...switchProbeConfig(),
      params: weekStart ? { week_start: weekStart } : undefined,
    }),

  book: (campaignId: string, startTime: string) =>
    api.post<DialBlockResponseI>(
      `/v1/campaign/${encodeURIComponent(campaignId)}/dial_blocks`,
      { start_time: startTime }
    ),

  /** The rep's own reschedule: a new start, a new campaign, or both. */
  move: (
    dialBlockId: string,
    changes: { start_time?: string; campaign_id?: string }
  ) =>
    api.patch<DialBlockResponseI>(
      `/v1/dial_blocks/${encodeURIComponent(dialBlockId)}`,
      changes
    ),

  /**
   * The caller's dependability score (B6). Behind the same switch: 404 with
   * 1035 also while the route isn't deployed yet.
   */
  getScore: () =>
    api.get<DialBlockScoreResponseI>("/v1/dial_block_score", {
      ...switchProbeConfig(),
    }),

  /** Asks the caller's LGA to lower one week's commitment (B2c). */
  requestAvailability: (body: CreateAvailabilityRequestI) =>
    api.post<AvailabilityRequestResponseI>(
      "/v1/dial_block_availability_requests",
      body
    ),

  /**
   * Asks the caller's LGA to change their usual commitment, from the week
   * after the LGA approves it (C-12 B).
   */
  requestUsualCommitment: (body: CreateUsualCommitmentRequestI) =>
    api.post<UsualCommitmentRequestResponseI>(
      "/v1/dial_block_usual_commitment_requests",
      body
    ),

  cancel: (dialBlockId: string) =>
    api.delete<DialBlockResponseI>(
      `/v1/dial_blocks/${encodeURIComponent(dialBlockId)}`
    ),
});
